import api from './api';
import {
  AirstripServiceConfig,
  AircraftServiceConfig,
  ExtraServiceDefinition,
  QuoteLegExtraServiceSelection,
  ResolvedExtraService,
  Leg,
} from '../types';

const unwrap = <T>(response: any): T => response?.data?.data || response?.data || [];

export const extraServicesApi = {
  async listDefinitions(includeInactive = false): Promise<ExtraServiceDefinition[]> {
    const response = await api.get('/extra-services/definitions', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return unwrap<ExtraServiceDefinition[]>(response);
  },

  async createDefinition(payload: Partial<ExtraServiceDefinition>): Promise<ExtraServiceDefinition> {
    const response = await api.post('/extra-services/definitions', payload);
    return unwrap<ExtraServiceDefinition>(response);
  },

  async updateDefinition(id: string, payload: Partial<ExtraServiceDefinition>): Promise<ExtraServiceDefinition> {
    const response = await api.patch(`/extra-services/definitions/${id}`, payload);
    return unwrap<ExtraServiceDefinition>(response);
  },

  async deleteDefinition(id: string): Promise<void> {
    await api.delete(`/extra-services/definitions/${id}`);
  },

  async listAirstripConfigs(airstripCodes: string[]): Promise<AirstripServiceConfig[]> {
    const response = await api.get('/extra-services/airstrip-configs', {
      params: airstripCodes.length ? { airstripCodes: airstripCodes.join(',') } : undefined,
    });
    return unwrap<AirstripServiceConfig[]>(response);
  },

  async bulkUpsertAirstripConfig(payload: {
    airstripCodes: string[];
    serviceId: string;
    enabled?: boolean;
    priceOverride?: number | null;
    minQty?: number | null;
    maxQty?: number | null;
  }): Promise<AirstripServiceConfig[]> {
    const response = await api.post('/extra-services/airstrip-configs/bulk', payload);
    return unwrap<AirstripServiceConfig[]>(response);
  },

  async listAircraftConfigs(aircraftIds: string[]): Promise<AircraftServiceConfig[]> {
    const response = await api.get('/extra-services/aircraft-configs', {
      params: aircraftIds.length ? { aircraftIds: aircraftIds.join(',') } : undefined,
    });
    return unwrap<AircraftServiceConfig[]>(response);
  },

  async bulkUpsertAircraftConfig(payload: {
    aircraftIds: string[];
    serviceId: string;
    enabled?: boolean;
    priceOverride?: number | null;
    minQty?: number | null;
    maxQty?: number | null;
  }): Promise<AircraftServiceConfig[]> {
    const response = await api.post('/extra-services/aircraft-configs/bulk', payload);
    return unwrap<AircraftServiceConfig[]>(response);
  },

  async resolveForLeg(leg: Partial<Leg>, aircraftId?: string): Promise<ResolvedExtraService[]> {
    const response = await api.post('/extra-services/resolve', {
      leg,
      aircraftId,
    });
    return unwrap<ResolvedExtraService[]>(response);
  },
};

export const toCostLineFromSelection = (item: QuoteLegExtraServiceSelection, leg: Leg) => {
  const pax = Math.max(0, leg.paxCount || 0);
  const hours = Math.max(0, leg.blockTimeMinutes || 0) / 60;
  let amount = item.unitPrice * Math.max(1, item.quantity || 1);
  if (item.pricingModel === 'PER_PAX') {
    amount = item.unitPrice * pax;
  }
  if (item.pricingModel === 'PER_HOUR') {
    amount = item.unitPrice * hours;
  }
  return {
    ...item,
    amount: Number(amount.toFixed(2)),
  };
};
